import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/api';

interface Season {
  id: string;
  name: string;
  competition?: {
    id: string;
    name: string;
  };
}

interface MatchGroup {
  id: string;
  name: string;
  description?: string;
  matchWeek?: number;
  isActive: boolean;
  seasonId: string;
  season?: Season;
  createdAt: string;
  updatedAt: string;
}

const AdminMatchGroups: React.FC = () => {
  const [matchGroups, setMatchGroups] = useState<MatchGroup[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingMatchGroup, setEditingMatchGroup] = useState<MatchGroup | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    matchWeek: '',
    isActive: true,
    seasonId: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (selectedSeason) {
      fetchMatchGroups();
    }
  }, [selectedSeason]);

  const fetchSeasons = async () => {
    try {
      const response = await apiClient.get('/admin/seasons');
      setSeasons(response.data);
      if (response.data.length > 0) {
        setSelectedSeason(response.data[0].id);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load seasons');
    }
  };

  const fetchMatchGroups = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/admin/matchgroups?season=${selectedSeason}`);
      setMatchGroups(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load match groups');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const matchGroupData = {
        name: formData.name,
        description: formData.description,
        matchWeek: formData.matchWeek ? parseInt(formData.matchWeek) : undefined
      };

      await apiClient.post(`/admin/seasons/${formData.seasonId || selectedSeason}/match-groups`, matchGroupData);
      setShowCreateModal(false);
      resetForm();
      fetchMatchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create match group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMatchGroup) return;
    
    setSubmitting(true);
    try {
      const matchGroupData = {
        name: formData.name,
        description: formData.description,
        matchWeek: formData.matchWeek ? parseInt(formData.matchWeek) : undefined,
        isActive: formData.isActive
      };

      await apiClient.put(`/admin/match-groups/${editingMatchGroup.id}`, matchGroupData);
      setEditingMatchGroup(null);
      setShowCreateModal(false);
      resetForm();
      fetchMatchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update match group');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (matchGroupId: string) => {
    if (!window.confirm('Are you sure you want to delete this match group? This will also affect any matches assigned to it.')) {
      return;
    }

    try {
      await apiClient.delete(`/admin/match-groups/${matchGroupId}`);
      fetchMatchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete match group');
    }
  };

  const handleEdit = (matchGroup: MatchGroup) => {
    setEditingMatchGroup(matchGroup);
    setFormData({
      name: matchGroup.name,
      description: matchGroup.description || '',
      matchWeek: matchGroup.matchWeek?.toString() || '',
      isActive: matchGroup.isActive,
      seasonId: matchGroup.seasonId
    });
    setShowCreateModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      matchWeek: '',
      isActive: true,
      seasonId: selectedSeason
    });
    setEditingMatchGroup(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowCreateModal(true);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    resetForm();
  };

  const toggleCurrentMatchGroup = async (matchGroupId: string, isCurrent: boolean) => {
    try {
      await apiClient.put(`/admin/match-groups/${matchGroupId}`, { isCurrent: !isCurrent });
      fetchMatchGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update match group');
    }
  };

  if (loading && matchGroups.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Loading match groups...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Manage Match Groups
          </h1>
          <Link 
            to="/admin" 
            className="text-blue-600 hover:text-blue-800 text-sm"
          >
            ← Back to Admin Dashboard
          </Link>
        </div>
        <button
          onClick={openCreateModal}
          className="btn-primary px-4 py-2"
          disabled={!selectedSeason}
        >
          Add New Match Group
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
          <button 
            onClick={() => setError('')}
            className="float-right text-red-700 hover:text-red-900"
          >
            ×
          </button>
        </div>
      )}

      {/* Season Filter */}
      <div className="card p-4 mb-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
            Season
          </label>
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
          >
            <option value="">Select a season...</option>
            {seasons.map((season) => (
              <option key={season.id} value={season.id}>
                {season.name} {season.competition && `(${season.competition.name})`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Match Groups Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Match Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Match Week
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Season
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {matchGroups.map((matchGroup) => (
                <tr key={matchGroup.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {matchGroup.name}
                        </div>
                        {matchGroup.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {matchGroup.description}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {matchGroup.matchWeek ? `Week ${matchGroup.matchWeek}` : 'No week set'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {matchGroup.season?.name || 'No season'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      matchGroup.isActive ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200' : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                    }`}>
                      {matchGroup.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleEdit(matchGroup)}
                        className="btn-secondary px-3 py-1 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(matchGroup.id)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {matchGroups.length === 0 && selectedSeason && (
            <div className="text-center py-8">
              <p className="text-xl text-gray-500">
                No match groups found for this season. Add your first match group!
              </p>
            </div>
          )}

          {!selectedSeason && (
            <div className="text-center py-8">
              <p className="text-xl text-gray-500">
                Please select a season to view match groups.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              {editingMatchGroup ? 'Edit Match Group' : 'Add New Match Group'}
            </h2>
            
            <form onSubmit={editingMatchGroup ? handleUpdate : handleCreate}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                    placeholder="e.g., Matchweek 1, Quarter Finals, etc."
                    maxLength={100}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    rows={2}
                    placeholder="Optional description of this match group"
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Match Week
                  </label>
                  <input
                    type="number"
                    value={formData.matchWeek}
                    onChange={(e) => setFormData({ ...formData, matchWeek: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    min="1"
                    placeholder="e.g., 1, 2, 3..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                    Active
                  </label>
                  <select
                    value={formData.isActive.toString()}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'true' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                    required
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {!editingMatchGroup && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                      Season *
                    </label>
                    <select
                      value={formData.seasonId}
                      onChange={(e) => setFormData({ ...formData, seasonId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-highlight focus:border-highlight"
                      required
                    >
                      <option value="">Select a season...</option>
                      {seasons.map((season) => (
                        <option key={season.id} value={season.id}>
                          {season.name} {season.competition && `(${season.competition.name})`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  className="btn-primary flex-1"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingMatchGroup ? 'Update Match Group' : 'Create Match Group'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary flex-1"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminMatchGroups; 